import * as React from "react";
import {
  Alert,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  FormControlLabel,
  Checkbox,
  Link,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import { fieldSx, panelSx, primaryButtonSx } from "../admin/AdminShell";
import { fetchMyProfile, signInWithPassword, signOut, signUpWithPassword } from "../../api/queries";
import { saveSession } from "../../api/session";
import { routeAfterAuth } from "../utils/workshopRouting";

type Mode = "register" | "login";

const MODES: Array<{ mode: Mode; label: string; icon: React.ElementType }> = [
  { mode: "register", label: "Register", icon: PersonAddAltRoundedIcon },
  { mode: "login", label: "Login", icon: LoginRoundedIcon },
];

// Same look as the admin area (AdminShell): flat gradient page, solid dark
// panel, white fields, violet primary button.
const submitButtonSx = primaryButtonSx({
  py: 1.1,
  fontSize: 14.5,
  color: "#FFFFFF",
  boxShadow: "none",
  "&:hover": { bgcolor: "#6D28D9", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#3E4258", color: "rgba(226,232,240,0.45)" },
});

const legalLinkSx = { color: "#C4B5FD", fontWeight: 800, textDecorationColor: "rgba(196,181,253,0.5)" };

// Format-only "is this a real-looking email" check, entirely client-side --
// no DNS lookup, no edge function, no backend dependency.
const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

function isLikelyValidEmail(value: string): boolean {
  return EMAIL_FORMAT_RE.test(value);
}

// No confirmation email is sent, so a mistyped address would go unnoticed.
// Registration guards against that with two client-side checks instead: the
// email has to be typed twice, and a domain that is a near-miss of one of
// these common providers gets a "did you mean" hint.
const COMMON_EMAIL_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "outlook.de",
  "hotmail.com",
  "hotmail.de",
  "live.com",
  "yahoo.com",
  "yahoo.de",
  "ymail.com",
  "icloud.com",
  "me.com",
  "aol.com",
  "mail.com",
  "email.com",
  "proton.me",
  "protonmail.com",
  "gmx.de",
  "gmx.net",
  "gmx.at",
  "gmx.ch",
  "gmx.com",
  "web.de",
  "t-online.de",
  "bluewin.ch",
];

// Levenshtein distance -- how many single-character edits separate a and b.
function editDistance(a: string, b: string): number {
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = row;
  }
  return prev[b.length];
}

// "anna@gmial.com" -> "anna@gmail.com"; null when the domain is one of the
// common ones already or isn't close to any of them.
function suggestEmailFix(email: string): string | null {
  const at = email.lastIndexOf("@");
  if (at < 1) return null;

  const domain = email.slice(at + 1);
  if (!domain || COMMON_EMAIL_DOMAINS.includes(domain)) return null;

  for (const known of COMMON_EMAIL_DOMAINS) {
    // Short domains (web.de, gmx.at) only tolerate one slip, otherwise they
    // start matching unrelated real domains.
    const tolerance = known.length <= 7 ? 1 : 2;
    if (editDistance(domain, known) <= tolerance) return `${email.slice(0, at)}@${known}`;
  }
  return null;
}

const MIN_PASSWORD_LENGTH = 8;

export default function Entry(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;

  const [mode, setMode] = React.useState<Mode>("register");
  const [email, setEmail] = React.useState("");
  const [emailRepeat, setEmailRepeat] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [legalAccepted, setLegalAccepted] = React.useState(false);

  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const emailNorm = email.trim().toLowerCase();
  const emailRepeatNorm = emailRepeat.trim().toLowerCase();
  const emailFix = isLikelyValidEmail(emailNorm) ? suggestEmailFix(emailNorm) : null;
  const repeatMismatch = emailRepeatNorm !== "" && emailRepeatNorm !== emailNorm;

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
  };

  // Shared tail of register and login: the Supabase Auth session exists at
  // this point, so load the profile row that belongs to it (created by the
  // "on_auth_user_created" trigger), remember who is signed in and move on.
  const enterApp = async (authUserId: string) => {
    const { data: profile, error: profileError } = await fetchMyProfile(authUserId);

    if (profileError || !profile) {
      // Signed in but no profile to work with -- don't leave a half-login behind.
      await signOut();
      setSubmitting(false);
      setError(profileError?.message ?? "Your account has no profile yet. Please contact the workshop team.");
      return;
    }

    setSubmitting(false);
    saveSession(emailNorm, profile.first_name, profile.last_name, profile.role);
    routeAfterAuth(emailNorm, handlePageChange);
  };

  const submitRegistration = async () => {
    setError(null);

    if (!isLikelyValidEmail(emailNorm)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (emailRepeatNorm !== emailNorm) {
      setError("The two email addresses don't match. Please check them.");
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Please choose a password with at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (!legalAccepted) {
      setError("Please accept the Privacy Policy and Terms & Conditions to continue.");
      return;
    }

    setSubmitting(true);

    const { data, error: signUpError } = await signUpWithPassword(emailNorm, password, { firstName, lastName });

    if (signUpError) {
      setSubmitting(false);
      if (signUpError.code === "user_already_exists" || /already registered/i.test(signUpError.message)) {
        setError("An account with this email already exists. Please log in instead.");
        setMode("login");
      } else {
        setError(signUpError.message);
      }
      return;
    }

    if (!data.session || !data.user) {
      // Only happens when "Confirm email" is switched back on in Supabase:
      // the account exists but can't sign in until a mailed link is clicked.
      setSubmitting(false);
      setError("Your account was created, but it has to be confirmed by email before you can log in.");
      return;
    }

    await enterApp(data.user.id);
  };

  const submitLogin = async () => {
    setError(null);

    if (!isLikelyValidEmail(emailNorm)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setSubmitting(true);

    const { data, error: signInError } = await signInWithPassword(emailNorm, password);

    if (signInError || !data.user) {
      setSubmitting(false);
      setError(
        signInError?.code === "invalid_credentials" || !signInError
          ? "Wrong email or password. If you have not set a password for this email yet, register with it first."
          : signInError.message
      );
      return;
    }

    await enterApp(data.user.id);
  };

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton edge="end" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? "Hide password" : "Show password"}>
        {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 3 },
        py: { xs: 4, md: 6 },
        background: "linear-gradient(160deg, #362654 0%, #2A2740 35%, #33384A 70%, #3A3F52 100%)",
      }}
    >
      <Box sx={{ ...panelSx, p: { xs: 2.25, sm: 3.5 }, width: "100%", maxWidth: 460, boxSizing: "border-box" }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              component="img"
              src="/logo.png"
              alt=""
              sx={{
                width: 40,
                height: 40,
                objectFit: "contain",
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.92)",
                p: 0.55,
              }}
            />
            <Typography
              sx={{ color: "#D1D5DB", fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", fontSize: 11 }}
            >
              Stellaris Mission Control
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            {MODES.map((item) => {
              const Icon = item.icon;
              const selected = mode === item.mode;
              return (
                <Button
                  key={item.mode}
                  onClick={() => switchMode(item.mode)}
                  aria-pressed={selected}
                  startIcon={<Icon sx={{ fontSize: 17 }} />}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: 13,
                    borderRadius: 999,
                    px: 1.75,
                    color: selected ? "#FFFFFF" : "#D1D5DB",
                    bgcolor: selected ? "#7C3AED" : "#2B2E40",
                    border: "1px solid",
                    borderColor: selected ? "#7C3AED" : "#3E4258",
                    "&:hover": { bgcolor: selected ? "#6D28D9" : "#363A4F" },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>

          {error && (
            <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {mode === "register" && (
            <>
              <Stack spacing={0.5}>
                <Typography sx={{ color: "#FFFFFF", fontWeight: 900, fontSize: { xs: 24, sm: 28 } }}>Register</Typography>
              </Stack>

              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                fullWidth
                autoFocus
                sx={fieldSx}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitRegistration();
                }}
              />

              {emailFix && (
                <Alert
                  severity="warning"
                  sx={{ borderRadius: 2, py: 0.25, alignItems: "center", overflowWrap: "anywhere" }}
                  action={
                    <Button
                      size="small"
                      onClick={() => {
                        setEmail(emailFix);
                        setEmailRepeat("");
                      }}
                      sx={{ textTransform: "none", fontWeight: 800, color: "inherit", whiteSpace: "nowrap" }}
                    >
                      Use this
                    </Button>
                  }
                >
                  Did you mean <strong>{emailFix}</strong>?
                </Alert>
              )}

              <TextField
                label="Repeat email"
                value={emailRepeat}
                onChange={(e) => setEmailRepeat(e.target.value)}
                fullWidth
                error={repeatMismatch}
                helperText={repeatMismatch ? "This doesn't match the email above." : undefined}
                sx={{ ...fieldSx, "& .MuiFormHelperText-root": { color: "#FCA5A5", fontWeight: 600 } }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitRegistration();
                }}
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                autoComplete="new-password"
                helperText={`At least ${MIN_PASSWORD_LENGTH} characters.`}
                InputProps={{ endAdornment: passwordAdornment }}
                sx={{ ...fieldSx, "& .MuiFormHelperText-root": { color: "rgba(226,232,240,0.6)" } }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitRegistration();
                }}
              />

              <TextField
                label="First name (optional)"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
                sx={fieldSx}
              />

              <TextField
                label="Last name (optional)"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
                sx={fieldSx}
              />

              <FormControlLabel
                sx={{ alignItems: "flex-start", mx: 0, mt: 0.5 }}
                control={
                  <Checkbox
                    checked={legalAccepted}
                    onChange={(e) => setLegalAccepted(e.target.checked)}
                    sx={{
                      color: "rgba(226,232,240,0.5)",
                      p: 0,
                      mr: 1.25,
                      mt: 0.25,
                      "&.Mui-checked": { color: "#A78BFA" },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 13, lineHeight: 1.5 }}>
                    I agree to the{" "}
                    <Link href="/privacy-policy" target="_blank" rel="noreferrer" sx={legalLinkSx}>
                      Privacy Policy
                    </Link>{" "}
                    and{" "}
                    <Link href="/terms-and-conditions" target="_blank" rel="noreferrer" sx={legalLinkSx}>
                      Terms & Conditions
                    </Link>
                    .
                  </Typography>
                }
              />

              <Button variant="contained" onClick={submitRegistration} disabled={!legalAccepted || submitting} sx={submitButtonSx}>
                {submitting ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : "Register"}
              </Button>
            </>
          )}

          {mode === "login" && (
            <>
              <Stack spacing={0.5}>
                <Typography sx={{ color: "#FFFFFF", fontWeight: 900, fontSize: { xs: 24, sm: 28 } }}>Login</Typography>
                <Typography sx={{ color: "rgba(226,232,240,0.6)", fontSize: 13.5 }}>
                  Enter the email and password you registered with.
                </Typography>
              </Stack>

              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                fullWidth
                autoFocus
                sx={fieldSx}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitLogin();
                }}
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                autoComplete="current-password"
                InputProps={{ endAdornment: passwordAdornment }}
                sx={fieldSx}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitLogin();
                }}
              />

              <Button variant="contained" onClick={submitLogin} disabled={submitting} sx={submitButtonSx}>
                {submitting ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : "Login"}
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
